import { useCallback, useRef, useState } from 'react'
import { wollemAgentApi, SseEventSchema } from '../api'
import type { ChatRecord, Message, SseEvent, ToolCall } from '../api'
import { useQueryClient } from '@tanstack/react-query'

const uid = () => Math.random().toString(36).slice(2)

const applyEvent = (msg: Message, event: SseEvent): Message => {
  switch (event.type) {
    case 'text-delta':
      return { ...msg, content: msg.content + event.delta }

    case 'reasoning-delta':
      return { ...msg, reasoning: msg.reasoning + event.delta }

    case 'tool-call-start': {
      const toolCall: ToolCall = {
        toolCallId: event.toolCallId,
        toolName: event.toolName,
        input: event.input,
        status: 'running',
      }
      return { ...msg, toolCalls: [...msg.toolCalls, toolCall] }
    }

    case 'tool-call-result':
      return {
        ...msg,
        toolCalls: msg.toolCalls.map((tc) =>
          tc.toolCallId === event.toolCallId
            ? { ...tc, result: event.result, isError: event.isError, status: 'complete' }
            : tc
        ),
      }

    default:
      return msg
  }
}

export const useChat = () => {
  const queryClient = useQueryClient()
  const chatIdRef = useRef<string | null>(null)

  const [chatId, setChatId] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  const sendMessage = useCallback(
    async (content: string) => {
      if (isStreaming) return

      const userMsg: Message = {
        id: uid(),
        role: 'user',
        content,
        reasoning: '',
        toolCalls: [],
        isStreaming: false,
      }
      const assistantId = uid()
      const assistantMsg: Message = {
        id: assistantId,
        role: 'assistant',
        content: '',
        reasoning: '',
        toolCalls: [],
        isStreaming: true,
      }

      setMessages((prev) => [...prev, userMsg, assistantMsg])
      setIsStreaming(true)

      abortRef.current = new AbortController()

      const onEvent = (event: SseEvent) => {
        setMessages((prev) =>
          prev.map((msg) => (msg.id === assistantId ? applyEvent(msg, event) : msg))
        )
      }

      const readStream = async (
        reader: ReadableStreamDefaultReader<Uint8Array>,
        decoder: TextDecoder,
        buffer: string
      ): Promise<void> => {
        const { value, done } = await reader.read()
        if (done) return

        const next = buffer + (value ? decoder.decode(value, { stream: true }) : '')
        const parts = next.split('\n\n')
        const remaining = parts.pop() ?? ''

        parts
          .map((p) => p.replace(/^data: /, '').trim())
          .filter((d) => d && d !== '[DONE]')
          .flatMap((d) => {
            try {
              const parsed = SseEventSchema.safeParse(JSON.parse(d))
              return parsed.success ? [parsed.data] : []
            } catch {
              return []
            }
          })
          .forEach(onEvent)

        return readStream(reader, decoder, remaining)
      }

      try {
        const resolvedId = chatIdRef.current ?? (await wollemAgentApi.createChat())
        if (!chatIdRef.current) {
          chatIdRef.current = resolvedId
          setChatId(resolvedId)
          queryClient.invalidateQueries({ queryKey: ['agent-chats'] })
        }

        const reader = await wollemAgentApi.streamMessage(
          resolvedId,
          content,
          abortRef.current.signal
        )
        await readStream(reader, new TextDecoder(), '')
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId
                ? { ...msg, content: 'Something went wrong. Please try again.', isStreaming: false }
                : msg
            )
          )
        }
      } finally {
        setIsStreaming(false)
        setMessages((prev) =>
          prev.map((msg) => (msg.id === assistantId ? { ...msg, isStreaming: false } : msg))
        )
      }
    },
    [isStreaming, queryClient]
  )

  const stop = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  const clear = useCallback(() => {
    chatIdRef.current = null
    setChatId(null)
    setMessages([])
  }, [])

  const loadMessages = useCallback((chat: ChatRecord) => {
    chatIdRef.current = chat.chat_id
    setChatId(chat.chat_id)
    setMessages(
      chat.messages.map((sm) => ({
        id: uid(),
        role: sm.role,
        content: sm.content,
        reasoning: '',
        toolCalls: [],
        isStreaming: false,
      }))
    )
  }, [])

  return { chatId, messages, isStreaming, sendMessage, stop, clear, loadMessages }
}
